import { ChatbotUIBasicProps } from '@/types/common';
import { ButtonGroupProps } from '@shopee/chatbot-ui/lib/components/ButtonGroup';
import { RichTextProps } from '@shopee/chatbot-ui/lib/components/RichText';
import React, { FunctionComponent, useState } from 'react';
import { composeTFEButtonGroupProps, composeTFEJumpNodeMapProps, ButtonValues } from './Message';
import { TimelineCard as TimelineCardV1, TimelineCardV2 } from '@shopee/chatbot-ui';
import { MilestoneValues } from '@shopee/chatbot-ui/lib/components/TimelineCard/Milestone';
import { TRACKING_SECTION, TRACKING_TARGET, TRACKING_TYPE } from '@/utils/tracking/constants';
import { TRACKING_MSG_TYPE } from '@/utils/constants';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { updateDialogueInfo } from '@/utils/sessionUtils';
import Webform, { WebFormProps } from '../Webform';
import { inject, observer } from 'mobx-react';
import { isBank } from '@/config/core';

export interface Props extends ChatbotUIBasicProps {
  title: RichTextProps;
  milestones: MilestoneValues[];
  buttonGroup: Omit<ButtonGroupProps, 'buttons'> & {
    buttons: ButtonValues[];
  };
}

const TimelineCard: FunctionComponent<Props> = (props) => {
  const [webformProps, setWebformProps] = useState<WebFormProps | null>(null);
  const submitCb = (btn_id: string) => {
    const newButtons = props.buttonGroup.buttons.map((btn, i) => {
      if (btn.button_id === btn_id) {
        return {
          ...btn,
          disabled: true,
        };
      } else {
        return btn;
      }
    });
    const newProps = {
      buttonGroup: {
        ...props.buttonGroup,
        buttons: newButtons,
      },
    };
    updateDialogueInfo(newProps, props.listPos);
  };
  const onClickButtonCb = (props: WebFormProps) => {
    setWebformProps(props);
  };

  const { jumpNodeMap: titleJumpNodeMap, linkTrackingData: titleLinkTrackingData } = composeTFEJumpNodeMapProps({
    ...props,
    jumpNodeMap: props.title.jumpNodeMap ?? {},
  });

  const milestones = props.milestones.map((v) => {
    const description = { ...v.description };
    if (description) {
      const jumpNodeMap = description.jumpNodeMap;
      if (jumpNodeMap) {
        description.jumpNodeMap = composeTFEJumpNodeMapProps({ ...props, jumpNodeMap });
      }
      const descriptionButtonGroup = description.buttonGroup!;
      description.buttonGroup = checkButtonsExist(descriptionButtonGroup)
        ? (composeTFEButtonGroupProps(
          {
            ...props,
            buttonGroup: descriptionButtonGroup as any,
          },
          onClickButtonCb,
        ) as ButtonGroupProps)
        : undefined;
    }

    return { ...v, description };
  });

  const buttonGroup = checkButtonsExist(props.buttonGroup)
    ? composeTFEButtonGroupProps(props as any, onClickButtonCb)
    : undefined;

  const trackingData = {
    trackingsection: TRACKING_SECTION[TRACKING_TYPE.TIMELINE_CARD],
    trackingtarget: TRACKING_TARGET[TRACKING_TYPE.EXPAND_BUTTON],
    trackingdataBasic: {
      node_point_id: props.nodePointId,
      kb_bot_id: props.kbBotId,
      tf_node_id: props.tfNodeId,
      tf_case_id: props.tfCaseId,
      chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
      chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
      if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      taskbot_id: props.taskbotInfo?.task_bot_id,
      taskflow_id: props.taskbotInfo?.flow_id,
    },
  };

  const TimelineCardComponent = isBank() ? TimelineCardV2 : TimelineCardV1;

  return (
    <>
      <TimelineCardComponent
        title={{
          ...props.title,
          jumpNodeMap: titleJumpNodeMap,
          linkTrackingData: titleLinkTrackingData,
        }}
        milestones={milestones as MilestoneValues[]}
        buttonGroup={buttonGroup as ButtonGroupProps}
        disabled={props.isHistory}
        trackingData={trackingData}
      />
      <Webform
        {...(webformProps || {})}
        submitCb={submitCb}
        isHistory={props.isHistory}
        historySessionId={props.historySessionId}
        sessionId={props.sessionId}
        historyDialogueId={props.historyDialogueId}
        dialogueId={props.dialogueId}
        listPos={props.listPos}
        onClosePopup={() => setWebformProps({ ...webformProps, active: false } as any)}
      />
    </>
  );
};

const checkButtonsExist = (buttonGroup: Props['buttonGroup'] | ButtonGroupProps) =>
  buttonGroup.buttons && buttonGroup.buttons.length > 0;

export default inject(getChildInject('initStore'))(observer(TimelineCard));
